/**
 * The AuthEvents class is a singleton that uses the observer pattern to manage authentication-related events.
 * It allows components to subscribe to specific events or all events, and notifies them when those events occur.
 */

export type AuthEvent = {
  event: string;
  data?: any;
  eventType?: 'info' | 'error';
};

class AuthEvents {
  // Singleton instance
  private static instance: AuthEvents | null = null;
  private readonly listeners: { [event: string]: Function[] } = {};
  private readonly allEventsListeners: Function[] = [];

  // registers an observer
  subscribe(event: string, listenerCallBackFn: Function) {
    if (event === '*') {
      this.allEventsListeners.push(listenerCallBackFn);
    } else {
      this.listeners[event] ??= [];
      this.listeners[event]?.push(listenerCallBackFn);
    }
  }

  // notify an observer
  publish({ event, data, eventType = 'info' }: AuthEvent) {
    const listeners = this.listeners[event];
    const notifiedListeners = new Set<Function>();

    // Notify specific event listeners
    if (listeners) {
      for (const listenerCallBackFn of listeners) {
        listenerCallBackFn({ event, data, eventType });
        notifiedListeners.add(listenerCallBackFn);
      }
    }

    // Notify all events listeners (but skip those already notified)
    for (const listenerCallBackFn of this.allEventsListeners) {
      if (!notifiedListeners.has(listenerCallBackFn)) {
        listenerCallBackFn({ event, data, eventType });
      }
    }
  }

  public static getInstance(): AuthEvents {
    AuthEvents.instance ??= new AuthEvents();
    return AuthEvents.instance;
  }
}

// Singleton instance
const authHub = AuthEvents.getInstance();
export { authHub as AuthEvents };
